import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CollaborativeNotebook from './components/CollaborativeNotebook';
import EvidenceAttachment from './components/EvidenceAttachment';
import MethodologyPanel from './components/MethodologyPanel';
import PeerReviewWorkflow from './components/PeerReviewWorkflow';
import PublishingControls from './components/PublishingControls';
import CaseManagement from './components/CaseManagement';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const FactCheckWorkbench = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeCase, setActiveCase] = useState(null);
  const [activePanel, setActivePanel] = useState('notebook');
  const [collaborators, setCollaborators] = useState([]);
  const [workspaceLayout, setWorkspaceLayout] = useState('default');

  // Mock active case data
  const mockActiveCase = {
    id: 'case_001',
    title: 'Climate Change Data Verification',
    claim: 'Global temperatures have increased by 2.5°C since 1990 according to new EU climate report',
    source: 'Social Media Post',
    submittedAt: new Date(Date.now() - 86400000),
    priority: 'high',
    status: 'in_progress',
    assignedTo: 'You',
    methodology: 'European Code of Standards',
    tags: ['climate', 'statistics', 'eu-policy'],
    deadline: new Date(Date.now() + 172800000),
    progress: 65
  };

  // Mock collaborators
  const mockCollaborators = [
    {
      id: 'collab_1',
      name: 'Dr. Elena Rossi',
      role: 'Primary Reviewer',
      status: 'active',
      lastSeen: new Date(Date.now() - 300000)
    },
    {
      id: 'collab_2',
      name: 'Marcus Weber',
      role: 'Secondary Reviewer',
      status: 'active',
      lastSeen: new Date(Date.now() - 600000)
    },
    {
      id: 'collab_3',
      name: 'Dr. Sarah Johnson',
      role: 'Expert Consultant',
      status: 'away',
      lastSeen: new Date(Date.now() - 3600000)
    }
  ];

  useEffect(() => {
    setActiveCase(mockActiveCase);
    setCollaborators(mockCollaborators);
  }, []);

  const handleCaseSelect = (caseData) => {
    setActiveCase(caseData);
  };

  const handleNewCase = () => {
    // Handle new case creation
    console.log('Creating new case...');
  };

  const handleSaveNotebook = (content) => {
    console.log('Saving notebook content:', content);
  };

  const handleAttachmentAdd = (attachment) => {
    console.log('Adding attachment:', attachment);
  };

  const handleStepComplete = (stepId) => {
    console.log('Step completed:', stepId);
  };

  const handleQualityCheck = (checkId, result) => {
    console.log('Quality check completed:', checkId, result);
  };

  const handleReviewSubmit = (reviewId, comment) => {
    console.log('Review submitted:', reviewId, comment);
  };

  const handleApprovalRequest = (requestId) => {
    console.log('Approval requested:', requestId);
  };

  const handlePublish = (verdictData) => {
    console.log('Publishing verdict:', verdictData);
  };

  const handleSaveDraft = (draftData) => {
    console.log('Saving draft:', draftData);
  };

  const panelComponents = {
    notebook: (
      <CollaborativeNotebook
        activeCase={activeCase}
        onSave={handleSaveNotebook}
        collaborators={collaborators}
      />
    ),
    evidence: (
      <EvidenceAttachment
        caseId={activeCase?.id}
        onAttachmentAdd={handleAttachmentAdd}
      />
    ),
    methodology: (
      <MethodologyPanel
        activeCase={activeCase}
        onStepComplete={handleStepComplete}
        onQualityCheck={handleQualityCheck}
      />
    ),
    review: (
      <PeerReviewWorkflow
        caseId={activeCase?.id}
        onReviewSubmit={handleReviewSubmit}
        onApprovalRequest={handleApprovalRequest}
      />
    ),
    publish: (
      <PublishingControls
        caseData={activeCase}
        onPublish={handlePublish}
        onSaveDraft={handleSaveDraft}
      />
    ),
    cases: (
      <CaseManagement
        onCaseSelect={handleCaseSelect}
        onNewCase={handleNewCase}
      />
    )
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex pt-16">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        
        <main className={`
          flex-1 transition-all duration-300
          ${sidebarCollapsed ? 'ml-16' : 'ml-64'}
        `}>
          {/* Workbench Header */}
          <div className="bg-card border-b border-border shadow-soft">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Wrench" size={24} className="text-primary" />
                  <div>
                    <h1 className="text-xl font-semibold text-foreground">
                      Fact-Check Workbench
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Collaborative research and verdict creation workspace
                    </p>
                  </div>
                </div>

                {activeCase && (
                  <div className="flex items-center space-x-2 px-3 py-2 bg-primary/10 rounded-lg">
                    <Icon name="FileText" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-primary">
                      {activeCase?.title}
                    </span>
                    <div className="px-2 py-1 bg-primary/20 rounded text-xs font-medium text-primary">
                      {activeCase?.progress}% complete
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-3">
                {/* Real-time Collaboration Status */}
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-2">
                    {collaborators?.slice(0, 3)?.map((collaborator, index) => (
                      <div
                        key={index}
                        className={`
                          w-8 h-8 rounded-full border-2 border-card flex items-center justify-center text-xs font-medium text-white
                          ${collaborator?.status === 'active' ? 'bg-success' : 'bg-muted'}
                        `}
                        title={`${collaborator?.name} - ${collaborator?.role}`}
                      >
                        {collaborator?.name?.charAt(0)}
                      </div>
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {collaborators?.filter(c => c?.status === 'active')?.length} active
                  </span>
                </div>

                {/* Layout Controls */}
                <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
                  <Button
                    variant={workspaceLayout === 'default' ? 'default' : 'ghost'}
                    size="sm"
                    iconName="Layout"
                    onClick={() => setWorkspaceLayout('default')}
                  />
                  <Button
                    variant={workspaceLayout === 'split' ? 'default' : 'ghost'}
                    size="sm"
                    iconName="Columns"
                    onClick={() => setWorkspaceLayout('split')}
                  />
                  <Button
                    variant={workspaceLayout === 'focus' ? 'default' : 'ghost'}
                    size="sm"
                    iconName="Maximize"
                    onClick={() => setWorkspaceLayout('focus')}
                  />
                </div>

                {/* Quick Actions */}
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Save"
                    onClick={() => handleSaveDraft({})}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Share"
                  >
                    Share
                  </Button>
                </div>
              </div>
            </div>

            {/* Panel Navigation */}
            <div className="flex border-t border-border">
              {[
                { id: 'cases', label: 'Cases', icon: 'FolderOpen' },
                { id: 'notebook', label: 'Notebook', icon: 'FileText' },
                { id: 'evidence', label: 'Evidence', icon: 'Paperclip' },
                { id: 'methodology', label: 'Methodology', icon: 'CheckSquare' },
                { id: 'review', label: 'Peer Review', icon: 'Users' },
                { id: 'publish', label: 'Publish', icon: 'Send' }
              ]?.map((panel) => (
                <button
                  key={panel?.id}
                  onClick={() => setActivePanel(panel?.id)}
                  className={`
                    flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-smooth
                    ${activePanel === panel?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                >
                  <Icon name={panel?.icon} size={16} />
                  <span>{panel?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Workspace */}
          <div className="p-6">
            <div className={`
              ${workspaceLayout === 'split' ? 'grid grid-cols-2 gap-6' :
                workspaceLayout === 'focus' ? 'max-w-4xl mx-auto' : 'max-w-full'}
              h-[calc(100vh-200px)]
            `}>
              {panelComponents?.[activePanel]}
            </div>
          </div>

          {/* Status Bar */}
          <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-soft z-30">
            <div className={`
              flex items-center justify-between px-6 py-2 text-sm
              ${sidebarCollapsed ? 'ml-16' : 'ml-64'}
            `}>
              <div className="flex items-center space-x-4 text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} />
                  <span>Last saved: {new Date()?.toLocaleTimeString()}</span>
                </div>
                
                {activeCase && (
                  <>
                    <div className="w-px h-4 bg-border" />
                    <div className="flex items-center space-x-1">
                      <Icon name="Target" size={14} />
                      <span>Case: {activeCase?.id}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>Due: {activeCase?.deadline?.toLocaleDateString()}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center space-x-4 text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                  <span>Connected</span>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Icon name="Shield" size={14} />
                  <span>EFCSN Compliant</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FactCheckWorkbench;